"use client";

import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

const DETAIL_OPEN_POINTER_MOVE_THRESHOLD = 12;
const MOBILE_ACTION_MENU_LONG_PRESS_MS = 600;
const DETAIL_OPEN_SUPPRESSION_AFTER_MENU_CLOSE_MS = 350;

let suppressOriginDetailOpenUntil = 0;

function suppressOriginDetailOpenBriefly() {
  suppressOriginDetailOpenUntil =
    Date.now() + DETAIL_OPEN_SUPPRESSION_AFTER_MENU_CLOSE_MS;
}

function isOriginDetailOpenSuppressed() {
  return Date.now() < suppressOriginDetailOpenUntil;
}

type UseOriginItemGesturesParams = {
  canOpenDetail: boolean;
  hasActions: boolean;
  isMobileDevice: boolean;
  onOpenDetail: () => void;
};

export default function useOriginItemGestures({
  canOpenDetail,
  hasActions,
  isMobileDevice,
  onOpenDetail,
}: UseOriginItemGesturesParams) {
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const didPointerMoveRef = useRef(false);
  const longPressMenuTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const longPressMenuOpenedRef = useRef(false);
  const mobileMenuContentRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [mobileActionMenuOpen, setMobileActionMenuOpen] = useState(false);

  const clearLongPressMenuTimer = useCallback(() => {
    if (!longPressMenuTimerRef.current) {
      return;
    }

    clearTimeout(longPressMenuTimerRef.current);
    longPressMenuTimerRef.current = null;
  }, []);

  const handlePointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    pointerStartRef.current = {
      x: event.clientX,
      y: event.clientY,
    };
    didPointerMoveRef.current = false;
  }, []);

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const pointerStart = pointerStartRef.current;

      if (!pointerStart) {
        return;
      }

      const deltaX = Math.abs(event.clientX - pointerStart.x);
      const deltaY = Math.abs(event.clientY - pointerStart.y);

      if (
        deltaX > DETAIL_OPEN_POINTER_MOVE_THRESHOLD ||
        deltaY > DETAIL_OPEN_POINTER_MOVE_THRESHOLD
      ) {
        didPointerMoveRef.current = true;
        clearLongPressMenuTimer();
      }
    },
    [clearLongPressMenuTimer],
  );

  const handleCardClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!canOpenDetail) {
        return;
      }

      if (
        didPointerMoveRef.current ||
        longPressMenuOpenedRef.current ||
        mobileActionMenuOpen ||
        isOriginDetailOpenSuppressed()
      ) {
        event.preventDefault();
        didPointerMoveRef.current = false;
        longPressMenuOpenedRef.current = false;
        pointerStartRef.current = null;
        return;
      }

      onOpenDetail();
    },
    [canOpenDetail, mobileActionMenuOpen, onOpenDetail],
  );

  const handleCardKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!canOpenDetail) {
        return;
      }

      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      onOpenDetail();
    },
    [canOpenDetail, onOpenDetail],
  );

  const handleMobileMenuLongPressStart = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (!isMobileDevice || !hasActions) {
        return;
      }

      clearLongPressMenuTimer();
      longPressMenuOpenedRef.current = false;
      pointerStartRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
      didPointerMoveRef.current = false;
      longPressMenuTimerRef.current = setTimeout(() => {
        longPressMenuOpenedRef.current = true;
        mobileMenuTriggerRef.current?.click();
      }, MOBILE_ACTION_MENU_LONG_PRESS_MS);
    },
    [clearLongPressMenuTimer, hasActions, isMobileDevice],
  );

  const handleMobileMenuLongPressEnd = useCallback(() => {
    clearLongPressMenuTimer();
  }, [clearLongPressMenuTimer]);

  const handleMobileActionMenuOpenChange = useCallback(
    (details: { open: boolean }) => {
      setMobileActionMenuOpen(details.open);

      if (!details.open) {
        suppressOriginDetailOpenBriefly();
        longPressMenuOpenedRef.current = false;
      }
    },
    [],
  );

  useEffect(() => {
    if (!mobileActionMenuOpen) {
      return;
    }

    const handleOutsidePointerDown = (event: globalThis.PointerEvent) => {
      const target = event.target;

      if (
        target instanceof Node &&
        mobileMenuContentRef.current?.contains(target)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      suppressOriginDetailOpenBriefly();
      setMobileActionMenuOpen(false);
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown, {
      capture: true,
    });

    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointerDown, {
        capture: true,
      });
    };
  }, [mobileActionMenuOpen]);

  return {
    handleCardClick,
    handleCardKeyDown,
    handleMobileActionMenuOpenChange,
    handleMobileMenuLongPressEnd,
    handleMobileMenuLongPressStart,
    handlePointerDown,
    handlePointerMove,
    mobileActionMenuOpen,
    mobileMenuContentRef,
    mobileMenuTriggerRef,
  };
}
